import React, { useState, useEffect } from "react";
import Modal from "../../../../components/ui/Modal";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import { v4 as uuidv4 } from "uuid";
import { PlusCircle, Trash2 } from "lucide-react";
import { featuredCreators, platformAdOpportunities } from "../../../../data/mockData";

const adTypeConfig = {
  "Image Banner": [
    "Prime Ad",
    "In-Feed Ad",
    "Commerce Banner",
    "Interstitial Ad",
  ],
  "Video Ad": ["In-Feed Ad", "Interstitial Ad"],
  "Text Link Ad": ["In-Feed Ad"],
};
const adTypes = Object.keys(adTypeConfig);

const AdminCreateAdModal = ({ isOpen, onClose, onSave, campaignToEdit }) => {
  const [source, setSource] = useState("Direct Deal");
  const [name, setName] = useState("");
  const [advertiser, setAdvertiser] = useState("");
  const [targetCreator, setTargetCreator] = useState("");
  const [budget, setBudget] = useState("");
  const [duration, setDuration] = useState("30");
  const [revShare, setRevShare] = useState("100");
  const [creatives, setCreatives] = useState([]);

  useEffect(() => {
    if (campaignToEdit) {
      setSource(campaignToEdit.source || "Direct Deal");
      setName(campaignToEdit.name || "");
      setAdvertiser(campaignToEdit.advertiser || campaignToEdit.client || "");
      setTargetCreator(campaignToEdit.targetCreator || "");
      setBudget(campaignToEdit.budget || "");
      setDuration(campaignToEdit.duration || "30");
      setRevShare(campaignToEdit.revShare || "100");
      setCreatives(
        campaignToEdit.creatives || [
          {
            id: uuidv4(),
            adType: "Image Banner",
            adZone: "Prime Ad",
            mediaUrl: "",
            link: "",
          },
        ]
      );
    } else {
      setSource("Direct Deal");
      setName("");
      setAdvertiser("");
      setTargetCreator("");
      setBudget("");
      setDuration("30");
      setRevShare("100");
      setCreatives([
        {
          id: uuidv4(),
          adType: "Image Banner",
          adZone: "Prime Ad",
          mediaUrl: "",
          link: "",
          impressions: 0,
          ctr: "0.0%",
          revenueETB: 0,
        },
      ]);
    }
  }, [campaignToEdit, isOpen]);

  const handleCreativeChange = (id, field, value) => {
    setCreatives(
      creatives.map((c) => {
        if (c.id === id) {
          const updatedCreative = { ...c, [field]: value };
          if (field === "adType") {
            updatedCreative.adZone = adTypeConfig[value][0];
          }
          return updatedCreative;
        }
        return c;
      })
    );
  };

  const addCreative = () => {
    setCreatives([
      ...creatives,
      {
        id: uuidv4(),
        adType: "Image Banner",
        adZone: "Prime Ad",
        mediaUrl: "",
        link: "",
      },
    ]);
  };

  const removeCreative = (id) => {
    if (creatives.length > 1) {
      setCreatives(creatives.filter((c) => c.id !== id));
    }
  };

  const handleSave = () => {
    if (!name || !advertiser || !targetCreator || creatives.some(c => !c.link)) {
      alert("Please fill out all fields and provide at least one creative link.");
      return;
    }

    const campaignData = {
      id: campaignToEdit ? campaignToEdit.id : Date.now(),
      name,
      advertiser,
      client: advertiser,
      targetCreator,
      budget,
      duration,
      revShare,
      status: campaignToEdit ? campaignToEdit.status : "Active",
      source,
      creatives,
    };
    onSave(campaignData);
  };

  return (
    <Modal title={campaignToEdit ? "Edit Ad Campaign" : "Create Ad Campaign"} isOpen={isOpen} onClose={onClose} size="2xl">
      <div className="space-y-6">
        {/* Source Toggle */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Campaign Source</label>
          <div className="flex p-1 bg-slate-100 rounded-lg w-full">
            <button
              onClick={() => setSource("Direct Deal")}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${
                source === "Direct Deal" ? "bg-white shadow text-primary" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Direct Deal
            </button>
            <button
              onClick={() => setSource("Platform-Sourced")}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${
                source === "Platform-Sourced" ? "bg-white shadow text-primary" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Platform-Sourced
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input label="Campaign Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Zemen Bank Q4" />
          <Input label="Advertiser" value={advertiser} onChange={(e) => setAdvertiser(e.target.value)} placeholder="e.g., Zemen Bank" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Target Creator</label>
          <select
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            value={targetCreator}
            onChange={(e) => setTargetCreator(e.target.value)}
          >
            <option value="">Select Creator</option>
            {featuredCreators.map((creator) => (
              <option key={creator.id} value={creator.displayName}>
                {creator.displayName} (@{creator.username})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Input label="Budget (ETB)" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="0.00" />
          <Input label="Duration (Days)" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="30" />
          <Input label="Rev. Share (%)" type="number" value={revShare} onChange={(e) => setRevShare(e.target.value)} placeholder="100" />
        </div>

        <div className="pt-4 border-t border-slate-100">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Ad Creatives</h3>
          <div className="space-y-4">
            {creatives.map((creative, index) => (
              <div key={creative.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50 relative group">
                {creatives.length > 1 && (
                  <button
                    onClick={() => removeCreative(creative.id)}
                    className="absolute -top-2 -right-2 bg-white border border-red-200 text-red-500 p-1.5 rounded-full shadow-sm hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Ad Type</label>
                    <select
                      value={creative.adType}
                      onChange={(e) => handleCreativeChange(creative.id, "adType", e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      {adTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Ad Zone</label>
                    <select
                      value={creative.adZone}
                      onChange={(e) => handleCreativeChange(creative.id, "adZone", e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      {adTypeConfig[creative.adType].map((zone) => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Input
                    label={creative.adType === "Text Link Ad" ? "Headline" : "Media URL"}
                    value={creative.mediaUrl || creative.headline || ""}
                    onChange={(e) => handleCreativeChange(creative.id, creative.adType === "Text Link Ad" ? "headline" : "mediaUrl", e.target.value)}
                    placeholder={creative.adType === "Text Link Ad" ? "Your ad text here..." : "https://..."}
                    className="text-xs"
                  />
                  <Input
                    label="Call-to-Action Link"
                    value={creative.link}
                    onChange={(e) => handleCreativeChange(creative.id, "link", e.target.value)}
                    placeholder="https://..."
                    className="text-xs"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={addCreative} className="mt-4 text-primary hover:bg-primary/5">
            <PlusCircle size={16} className="mr-2" /> Add Another Creative
          </Button>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Campaign</Button>
        </div>
      </div>
    </Modal>
  );
};

export default AdminCreateAdModal;
